import React, { useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  ColumnDef,
  flexRender,
  Table,
} from "@tanstack/react-table";
import { useGet } from "../../../../hooks/useGet";
import { useFilterContext } from "../../../../context/FilterContext";
import { ApiResponse } from "../../../../utils/types";
import AdminPurchaseExportButton from "./AdminPurchaseExportButton";
import AdminPurchaseTablePagination from "./AdminPurchaseTablePagination";
import AdminPurchaseDetailsModal from "./AdminPurchaseDetailsModal";

export interface AdminPurchaseData {
  id: number;
  network: string;
  msisdn: string;
  user_name: string;
  amount: number;
  status: string;
  created_at: string;
  mo: string;
}

export type AdminPurchaseApiResponse = ApiResponse<AdminPurchaseData>;

const AdminPurchaseTable: React.FC = () => {
  const [pageIndex, setPageIndex] = useState(0);
  const [selectedRow, setSelectedRow] = useState<AdminPurchaseData | null>(null);

  const {
    appliedCompany,
    appliedNetwork,
    appliedPhoneNumber,
    appliedStartDate,
    appliedEndDate,
  } = useFilterContext();

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (appliedNetwork) params.append("network", appliedNetwork);
    if (appliedPhoneNumber) params.append("msisdn", appliedPhoneNumber);
    if (appliedCompany) params.append("company", appliedCompany);
    if (appliedStartDate) params.append("startDate", appliedStartDate);
    if (appliedEndDate) params.append("endDate", appliedEndDate);
    params.append("page", (pageIndex + 1).toString());
    return params.toString();
  }, [
    appliedNetwork,
    appliedPhoneNumber,
    appliedCompany,
    appliedStartDate,
    appliedEndDate,
    pageIndex,
  ]);

  const { data, isLoading, error } = useGet<AdminPurchaseApiResponse>(
    `/admin/metrics/purchases?${queryParams}`,
    ["admin_purchase_table", queryParams]
  );

  const columns = useMemo<ColumnDef<AdminPurchaseData>[]>(
    () => [
      {
        accessorKey: "user_name",
        header: "Company",
        cell: (info) => {
          const companyName = info.getValue() as string;
          const initials = companyName
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase())
            .join("")
            .slice(0, 2); // Get first two initials
          return (
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 text-white text-xs font-semibold">
                {initials}
              </div>
              <span className="text-sm font-medium">{companyName}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "msisdn",
        header: "Phone Number",
        cell: (info) => (
          <div className="font-mono text-sm">{info.getValue() as string}</div>
        ),
      },
      {
        accessorKey: "mo",
        header: "Mobile Originated (MO)",
        cell: (info) => (
          <div
            className="truncate max-w-[200px]"
            title={info.getValue() as string}
          >
            <span className="bg-green-50 text-green-800 px-2 py-1 rounded text-xs font-medium">
              {info.getValue() as string}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "amount",
        header: "Amount",
        cell: (info) => (
          <div className="text-sm font-medium">{info.getValue() as number}</div>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (info) => {
          const status = info.getValue() as string;
          let bgColor = "bg-gray-100";
          let textColor = "text-gray-800";
          if (status === "completed") {
            bgColor = "bg-green-100";
            textColor = "text-green-800";
          } else if (status === "pending") {
            bgColor = "bg-yellow-100";
            textColor = "text-yellow-800";
          } else if (status === "failed") {
            bgColor = "bg-red-100";
            textColor = "text-red-800";
          }
          return (
            <span
              className={`px-2 py-1 rounded text-xs font-semibold ${bgColor} ${textColor}`}
            >
              {status}
            </span>
          );
        },
      },
      {
        accessorKey: "network",
        header: "Network",
        cell: (info) => (
          <span
            className={`px-2 py-1 rounded text-xs font-semibold ${
              (info.getValue() as string) === "MTN"
                ? "bg-yellow-100 text-yellow-800"
                : (info.getValue() as string) === "Airtel"
                ? "bg-red-100 text-red-800"
                : (info.getValue() as string) === "Glo"
                ? "bg-green-100 text-green-800"
                : (info.getValue() as string) === "9Mobile"
                ? "bg-purple-100 text-purple-800"
                : "bg-gray-100 text-gray-800"
            }`}
          >
            {info.getValue() as string}
          </span>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Timestamp",
        cell: (info) => (
          <div className="text-xs text-gray-600">
            {new Date(info.getValue() as string).toLocaleString()}
          </div>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: data?.data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount: data?.last_page || 1,
    state: {
      pagination: {
        pageIndex,
        pageSize: 15,
      },
    },
    onPaginationChange: (updater) => {
      const newState =
        typeof updater === "function"
          ? updater({ pageIndex, pageSize: 15 })
          : updater;
      setPageIndex(newState.pageIndex);
    },
  });

  if (isLoading)
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading purchases...</span>
      </div>
    );

  if (error)
    return (
      <div className="text-center py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-md mx-auto">
          <div className="text-red-600 font-medium">Error loading data</div>
          <div className="text-red-500 text-sm mt-1">{error.message}</div>
        </div>
      </div>
    );

  return (
    <div className="p-4 sm:p-6 max-w-full sm:max-w-7xl mx-auto">
      <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-200">
        {/* Export Button */}
        <div className="p-4 border-b border-gray-200">
          <AdminPurchaseExportButton data={data} />
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-800">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-white uppercase tracking-wider"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No purchases found
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, index) => (
                  <tr
                    key={row.id}
                    className={`transition-colors duration-150 cursor-pointer ${
                      index % 2 === 0 ? "bg-gray-50" : "bg-white"
                    } hover:bg-blue-50`}
                    onClick={() => setSelectedRow(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-800"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <AdminPurchaseTablePagination
          table={table}
          data={data}
          setPageIndex={setPageIndex}
        />
      </div>

      {/* Purchase Details Modal */}
      {selectedRow && (
        <AdminPurchaseDetailsModal
          selectedRow={selectedRow}
          onClose={() => setSelectedRow(null)}
        />
      )}
    </div>
  );
};

export default AdminPurchaseTable;